/* eslint-disable no-console */
import type { AccessToken, IDToken, TokenManagerError } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { AuthSettingsContext } from './auth-provider';

/**
 * Enhanced authentication hook that provides a stable API for Okta authentication
 * Follows React best practices with proper memoization and error handling
 */
export const useAuth = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const authSettings = useContext(AuthSettingsContext);
  // eslint-disable-next-line @typescript-eslint/no-restricted-types
  const errorListenerRef = useRef<((error: TokenManagerError) => void) | null>(null);

  // Helper to check token expiration with proper type checking
  const isTokenValid = useCallback((token?: { expiresAt?: number }): boolean => {
    if (!token?.expiresAt) {
      return false;
    }

    return token.expiresAt > Math.floor(Date.now() / 1000);
  }, []);

  // Enhanced token validation with better error handling
  const validateTokens = useCallback(async (): Promise<boolean> => {
    try {
      const [accessToken, idToken] = await Promise.all([
        oktaAuth.tokenManager.get('accessToken') as Promise<AccessToken | undefined>,
        oktaAuth.tokenManager.get('idToken') as Promise<IDToken | undefined>,
      ]);

      if (!accessToken || !idToken) {
        console.warn('Missing tokens during validation');
        return false;
      }

      return isTokenValid(accessToken) && isTokenValid(idToken);
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }, [oktaAuth, isTokenValid]);

  // Enhanced login with better error handling
  const login = useCallback(
    async (originalPath?: string) => {
      try {
        if (originalPath) {
          await oktaAuth.signInWithRedirect({ originalUri: originalPath });
        } else {
          // Call without options so existing oktaAuth.originalUri (set earlier) is preserved
          await oktaAuth.signInWithRedirect();
        }
      } catch (error) {
        console.error('Login failed:', error);
        throw error;
      }
    },
    [oktaAuth],
  );

  // Expose setting originalUri explicitly (e.g., before redirecting to /login route)
  const setOriginalUri = useCallback(
    (path: string) => {
      try {
        oktaAuth.setOriginalUri(path);
      } catch (error) {
        console.error('Failed to set originalUri:', error);
      }
    },
    [oktaAuth],
  );

  // Enhanced logout that appends a one-time ?from=<path> query param so login can restore it.
  // We intentionally avoid any storage. Only relative, safe paths are propagated.
  const logout = useCallback(
    async (clearServerCache?: () => Promise<void>) => {
      try {
        // Call server logout to clear backend cache if provided
        if (clearServerCache) {
          try {
            await clearServerCache();
          } catch (error) {
            console.error('Failed to clear server cache during logout:', error);
            // Continue with logout even if server cache clear fails
          }
        }

        const currentPath = window.location.pathname + window.location.search;
        const isSafePath =
          currentPath.startsWith('/') &&
          !currentPath.startsWith('/login') &&
          !currentPath.startsWith('/auth');

        const baseTarget = authSettings?.logoutRedirectPath || '/login';
        const targetWithQuery = isSafePath
          ? `${baseTarget}${baseTarget.includes('?') ? '&' : '?'}from=${encodeURIComponent(
              currentPath,
            )}`
          : baseTarget;

        oktaAuth.tokenManager.clear();
        await oktaAuth.signOut({
          postLogoutRedirectUri: `${window.location.origin}${targetWithQuery}`,
        });
      } catch (error) {
        console.error('Logout failed:', error);
        throw error;
      }
    },
    [oktaAuth, authSettings?.logoutRedirectPath],
  );

  // Enhanced token retrieval with error handling
  const getAccessToken = useCallback(async (): Promise<string | undefined> => {
    try {
      const token = (await oktaAuth.tokenManager.get('accessToken')) as AccessToken | undefined;
      return token?.accessToken;
    } catch (error) {
      console.error('Error fetching access token:', error);
      throw error;
    }
  }, [oktaAuth]);

  const getIdToken = useCallback(async (): Promise<string | undefined> => {
    try {
      const token = (await oktaAuth.tokenManager.get('idToken')) as IDToken | undefined;
      return token?.idToken;
    } catch (error) {
      console.error('Error fetching ID token:', error);
      throw error;
    }
  }, [oktaAuth]);

  // Enhanced user info retrieval
  const getUser = useCallback(async () => {
    try {
      return await oktaAuth.getUser();
    } catch (error) {
      console.error('Error fetching user info:', error);
      throw error;
    }
  }, [oktaAuth]);

  // Enhanced token renewal with proper error handling
  const renewToken = useCallback(
    async (tokenType: 'accessToken' | 'idToken' = 'accessToken') => {
      try {
        return await oktaAuth.tokenManager.renew(tokenType);
      } catch (error) {
        console.error(`Token renewal failed for ${tokenType}:`, error);
        throw error;
      }
    },
    [oktaAuth],
  );

  // New: Get all tokens at once
  const getTokens = useCallback(async () => {
    try {
      return await oktaAuth.tokenManager.getTokens();
    } catch (error) {
      console.error('Error fetching tokens:', error);
      throw error;
    }
  }, [oktaAuth]);

  // New: Check if token has expired
  const hasTokenExpired = useCallback(
    (tokenType: 'accessToken' | 'idToken') => {
      try {
        const tokenStorage = oktaAuth.tokenManager.getTokensSync();
        const token = tokenStorage[tokenType];
        return token ? oktaAuth.tokenManager.hasExpired(token) : true;
      } catch (error) {
        console.error(`Error checking token expiration for ${tokenType}:`, error);
        return true;
      }
    },
    [oktaAuth],
  );

  // New: Revoke tokens
  const revokeAccessToken = useCallback(async () => {
    try {
      await oktaAuth.revokeAccessToken();
    } catch (error) {
      console.error('Error revoking access token:', error);
      throw error;
    }
  }, [oktaAuth]);

  const revokeRefreshToken = useCallback(async () => {
    try {
      await oktaAuth.revokeRefreshToken();
    } catch (error) {
      console.error('Error revoking refresh token:', error);
      throw error;
    }
  }, [oktaAuth]);

  // Set up token manager error listener
  useEffect(() => {
    const handleTokenError = (error: TokenManagerError) => {
      console.error('TokenManager error:', {
        errorCode: error.errorCode,
        errorSummary: error.errorSummary,
        tokenKey: error.tokenKey,
        message: error.message,
      });
    };

    // Remove previous listener if it exists
    if (errorListenerRef.current) {
      oktaAuth.tokenManager.off('error', errorListenerRef.current);
    }

    // Add new listener
    errorListenerRef.current = handleTokenError;
    oktaAuth.tokenManager.on('error', handleTokenError);

    return () => {
      if (errorListenerRef.current) {
        oktaAuth.tokenManager.off('error', errorListenerRef.current);
      }
    };
  }, [oktaAuth]);

  // Derive pending: before first authState OR unresolved state without tokens or error
  const isPending =
    !authState ||
    (typeof authState.isAuthenticated === 'undefined' &&
      !authState.idToken &&
      !authState.accessToken &&
      !authState.error);

  // Memoize the returned object with comprehensive API
  return useMemo(
    () => ({
      // Auth state
      isAuthenticated: Boolean(authState?.isAuthenticated),
      isPending,
      user: authState?.idToken?.claims ?? null,
      authState,

      // Core auth methods
      login,
      logout,
      setOriginalUri,

      // Token management
      getAccessToken,
      getIdToken,
      getTokens,
      getUser,
      renewToken,
      validateTokens,
      hasTokenExpired,

      // Token revocation
      revokeAccessToken,
      revokeRefreshToken,

      // Utility methods
      isTokenValid,
    }),
    [
      authState,
      isPending,
      login,
      logout,
      setOriginalUri,
      getAccessToken,
      getIdToken,
      getTokens,
      getUser,
      renewToken,
      validateTokens,
      hasTokenExpired,
      revokeAccessToken,
      revokeRefreshToken,
      isTokenValid,
    ],
  );
};
